/**
 * Catálogo de permissões do sistema.
 *
 * Permissões são fixas no código; perfis (roles) agrupam permissões e são editáveis nas telas.
 * Cada módulo novo acrescenta as suas aqui. Nunca renomeie um código já em uso: crie um novo
 * e migre os perfis (ver docs/adr/0006-login-e-permissoes.md).
 */

export type LocalizedText = { 'pt-BR': string; en: string };

export interface PermissionDefinition {
  code: string;
  module: string;
  label: LocalizedText;
  /** Permissões que só fazem sentido no grupo inteiro (não por empresa/filial). */
  groupOnly?: boolean;
  /**
   * Ações sensíveis (aprovações, bloqueios, dados financeiros, pessoais e de administração).
   * Vão exigir a segunda etapa de login (entrega S2, ADR 0013).
   */
  sensitive?: boolean;
  /**
   * Nunca vem automaticamente no perfil Administrador: só por atribuição explícita a quem foi
   * autorizado (ex.: confirmar contas bancárias, só a Tesouraria; ADR 0016). Um administrador
   * também pode recebê-la, por outro perfil atribuído a ele.
   */
  explicitOnly?: boolean;
}

export const PERMISSION_MODULES: Record<string, LocalizedText> = {
  core: { 'pt-BR': 'Estrutura do grupo', en: 'Group structure' },
  iam: { 'pt-BR': 'Usuários e acessos', en: 'Users and access' },
  audit: { 'pt-BR': 'Auditoria', en: 'Audit' },
  help: { 'pt-BR': 'Ajuda', en: 'Help' },
  purchasing: { 'pt-BR': 'Compras', en: 'Purchasing' },
  partner: { 'pt-BR': 'Parceiros', en: 'Partners' },
  knowledge: { 'pt-BR': 'Central de Conhecimento', en: 'Knowledge Center' },
  approval: { 'pt-BR': 'Aprovações e governança', en: 'Approvals and governance' },
  structure: { 'pt-BR': 'Estrutura Funcional', en: 'Functional structure' },
  legal: { 'pt-BR': 'Jurídico', en: 'Legal' },
  facilities: { 'pt-BR': 'Facilities', en: 'Facilities' },
  asset: { 'pt-BR': 'Patrimônio', en: 'Assets' },
  it: { 'pt-BR': 'Tecnologia da Informação', en: 'Information Technology' },
  fpa: { 'pt-BR': 'FP&A (planejamento e análise financeira)', en: 'FP&A (financial planning)' },
  feedback: { 'pt-BR': 'Bugs e melhorias', en: 'Bugs and improvements' },
  intranet: { 'pt-BR': 'Intranet', en: 'Intranet' },
  executive: { 'pt-BR': 'Dashboard executivo', en: 'Executive dashboard' },
  people: { 'pt-BR': 'Pessoas e colaboradores', en: 'People and employees' },
  career: { 'pt-BR': 'Cargos e carreiras', en: 'Jobs and careers' },
  masterdata: { 'pt-BR': 'Importações de Dados Mestres', en: 'Master data imports' },
  sla: { 'pt-BR': 'Prazos de atendimento (SLA)', en: 'Service levels (SLA)' },
  finance: { 'pt-BR': 'Financeiro e Tesouraria', en: 'Finance and Treasury' },
};

export const PERMISSIONS = [
  {
    code: 'core.legal_entity.read',
    module: 'core',
    label: { 'pt-BR': 'Ver empresas', en: 'View legal entities' },
  },
  {
    code: 'core.legal_entity.manage',
    module: 'core',
    label: { 'pt-BR': 'Cadastrar e alterar empresas', en: 'Create and edit legal entities' },
    groupOnly: true,
  },
  {
    code: 'core.branch.read',
    module: 'core',
    label: { 'pt-BR': 'Ver filiais', en: 'View branches' },
  },
  {
    code: 'core.branch.manage',
    module: 'core',
    label: { 'pt-BR': 'Cadastrar e alterar filiais', en: 'Create and edit branches' },
  },
  {
    // Procuradores e Sócios (ADR 0033): todas explícitas, nunca automáticas.
    code: 'core.representative.read',
    module: 'core',
    label: {
      'pt-BR': 'Ver procuradores, sócios e poderes (sem documentos pessoais)',
      en: 'View attorneys, partners and powers (without personal documents)',
    },
    explicitOnly: true,
  },
  {
    code: 'core.representative.manage',
    module: 'core',
    label: {
      'pt-BR': 'Cadastrar procuradores, sócios, poderes e documentos comprobatórios',
      en: 'Register attorneys, partners, powers and supporting documents',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'core.representative.validate',
    module: 'core',
    label: {
      'pt-BR': 'Validar, suspender e revogar poderes (Jurídico; nunca os próprios)',
      en: 'Validate, suspend and revoke powers (Legal; never your own)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'core.representative.read_sensitive',
    module: 'core',
    label: {
      'pt-BR': 'Ver CPF e documentos societários confidenciais dos representantes',
      en: "View representatives' tax IDs and confidential corporate documents",
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'core.department.manage',
    module: 'core',
    label: {
      'pt-BR': 'Cadastrar e alterar áreas e seus gestores',
      en: 'Create and edit departments and their managers',
    },
  },
  {
    // Catálogo corporativo de processos e atividades (ADR 0061): Dados Mestres, no grupo inteiro.
    code: 'core.process.manage',
    module: 'core',
    label: {
      'pt-BR': 'Administrar o catálogo de processos e atividades (SLA)',
      en: 'Manage the process and activity catalog (SLA)',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    // Calendários de expediente e feriados (ADR 0061): os calendários no grupo; a atribuição a
    // uma empresa ou filial também pode ser feita por quem tem a permissão naquela empresa.
    code: 'core.calendar.manage',
    module: 'core',
    label: {
      'pt-BR': 'Administrar calendários de expediente e feriados',
      en: 'Manage working calendars and holidays',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    // Propor políticas de SLA (rascunhos e versões). Aprovar não é permissão: é competência
    // formal no motor de Aprovações (ADR 0061, D1 e D5).
    code: 'sla.policy.propose',
    module: 'sla',
    label: {
      'pt-BR': 'Configurar e ativar os prazos (regras de SLA) da área, com motivo',
      en: "Configure and activate the area's deadlines (SLA rules), with a reason",
    },
    explicitOnly: true,
  },
  {
    // Indicadores agregados do SLA (ADR 0061, seção 9): números, nunca itens; sigilosos protegidos.
    code: 'sla.report.read',
    module: 'sla',
    label: {
      'pt-BR': 'Ver os indicadores de prazos (SLA) agregados',
      en: 'View aggregated service level (SLA) indicators',
    },
    explicitOnly: true,
  },
  {
    code: 'sla.report.export',
    module: 'sla',
    label: {
      'pt-BR': 'Exportar indicadores de prazos (SLA)',
      en: 'Export service level (SLA) indicators',
    },
    explicitOnly: true,
  },
  {
    // Auditoria corporativa do SLA: metadados de alertas, apurações, revisões e exceções, sem o
    // conteúdo dos itens (o sigilo de cada módulo prevalece).
    code: 'sla.audit.read',
    module: 'sla',
    label: {
      'pt-BR': 'Auditar os prazos (SLA): alertas, apurações, revisões e exceções',
      en: 'Audit service levels (SLA): alerts, closings, revisions and exceptions',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    // Pedir revisão excepcional de um prazo. Decidir não é permissão: é competência formal no motor
    // de Aprovações (ADR 0061, D5), ainda indisponível.
    code: 'sla.revision.request',
    module: 'sla',
    label: {
      'pt-BR': 'Pedir revisão excepcional de prazo (SLA)',
      en: 'Request an exceptional deadline revision (SLA)',
    },
    explicitOnly: true,
  },
  {
    // Exceção técnica justificada (legado, correção): nunca apaga atraso nem evento.
    code: 'sla.exception.record',
    module: 'sla',
    label: {
      'pt-BR': 'Registrar exceção técnica de prazo (SLA)',
      en: 'Record a technical deadline exception (SLA)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    // Cadastro corporativo de Locais (prédio, andar e ambiente de cada filial; ADR 0027).
    code: 'core.location.manage',
    module: 'core',
    label: {
      'pt-BR': 'Cadastrar, alterar e inativar locais (prédios, andares e ambientes)',
      en: 'Create, edit and deactivate locations (buildings, floors and spaces)',
    },
  },
  {
    code: 'core.cost_center.read',
    module: 'core',
    label: { 'pt-BR': 'Ver centros de custo', en: 'View cost centers' },
  },
  {
    code: 'core.cost_center.manage',
    module: 'core',
    label: {
      'pt-BR': 'Cadastrar, alterar e inativar centros de custo (administradores autorizados)',
      en: 'Create, edit and deactivate cost centers (authorized administrators)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'core.currency.read',
    module: 'core',
    label: { 'pt-BR': 'Ver moedas e câmbio', en: 'View currencies and exchange rates' },
  },
  {
    code: 'core.currency.manage',
    module: 'core',
    label: {
      'pt-BR': 'Cadastrar moedas e taxas de câmbio',
      en: 'Manage currencies and exchange rates',
    },
    groupOnly: true,
  },
  {
    code: 'iam.user.read',
    module: 'iam',
    label: { 'pt-BR': 'Ver usuários', en: 'View users' },
    groupOnly: true,
  },
  {
    code: 'iam.user.manage',
    module: 'iam',
    label: {
      'pt-BR': 'Cadastrar e desativar usuários',
      en: 'Create and deactivate users',
    },
    groupOnly: true,
    sensitive: true,
  },
  {
    code: 'iam.access.grant',
    module: 'iam',
    label: {
      'pt-BR':
        'Conceder e retirar perfis no próprio escopo, conforme a matriz; pedir e decidir aprovações',
      en: 'Grant and remove roles within own scope, per the matrix; request and decide approvals',
    },
    sensitive: true,
  },
  {
    code: 'iam.role.read',
    module: 'iam',
    label: { 'pt-BR': 'Ver perfis de acesso', en: 'View roles' },
    groupOnly: true,
  },
  {
    code: 'iam.role.manage',
    module: 'iam',
    label: { 'pt-BR': 'Criar e alterar perfis de acesso', en: 'Create and edit roles' },
    groupOnly: true,
    sensitive: true,
  },
  {
    code: 'audit.log.read',
    module: 'audit',
    label: {
      'pt-BR':
        'Ver o histórico de alterações administrativas (o histórico dos dados confidenciais fica em cada módulo)',
      en: 'View the history of administrative changes (confidential data history stays in each module)',
    },
    groupOnly: true,
    sensitive: true,
  },
  {
    code: 'audit.access.read',
    module: 'audit',
    label: {
      'pt-BR': 'Ver os eventos de segurança (entradas, recusas e operações sensíveis)',
      en: 'View security events (sign-ins, refusals and sensitive operations)',
    },
    groupOnly: true,
    sensitive: true,
  },
  {
    code: 'help.article.manage',
    module: 'help',
    label: {
      'pt-BR': 'Criar, editar, ocultar e excluir artigos da Ajuda',
      en: 'Create, edit, hide and delete Help articles',
    },
    groupOnly: true,
  },
  {
    code: 'purchasing.request.create',
    module: 'purchasing',
    label: {
      'pt-BR': 'Pedir compras e acompanhar os próprios pedidos',
      en: 'Request purchases and follow your own requests',
    },
  },
  {
    code: 'purchasing.request.manage',
    module: 'purchasing',
    label: {
      'pt-BR':
        'Cuidar da fila de compras: assumir, conduzir, cotar, emitir o pedido ao fornecedor e comentar',
      en: 'Handle the purchasing queue: take, move forward, quote, issue the supplier order and comment',
    },
  },
  {
    code: 'purchasing.receipt.register',
    module: 'purchasing',
    label: {
      'pt-BR':
        'Registrar a chegada das compras (protocolo da portaria e recebimento físico), sem ver preços',
      en: 'Record purchase deliveries (gate log and physical receipt), without seeing prices',
    },
  },
  {
    code: 'purchasing.quote_rule.manage',
    module: 'purchasing',
    label: {
      'pt-BR': 'Configurar as regras de quantidade mínima de propostas nas cotações',
      en: 'Configure the minimum number of proposals rules for quotations',
    },
    sensitive: true,
  },
  {
    code: 'partner.partner.read',
    module: 'partner',
    label: {
      'pt-BR': 'Ver os parceiros vinculados às empresas do escopo',
      en: 'View partners linked to the companies in scope',
    },
  },
  {
    code: 'partner.partner.manage',
    module: 'partner',
    label: {
      'pt-BR':
        'Cadastrar e alterar parceiros, contatos e referências externas, e pedir vínculo com a empresa',
      en: 'Create and edit partners, contacts and external references, and request a link to the company',
    },
  },
  {
    code: 'partner.partner.approve',
    module: 'partner',
    label: {
      'pt-BR': 'Homologar, recusar, inativar e reativar parceiros (no grupo: o cadastro inteiro)',
      en: 'Approve, reject, deactivate and reactivate partners (group scope: the whole record)',
    },
    sensitive: true,
  },
  {
    code: 'partner.restriction.commercial',
    module: 'partner',
    label: {
      'pt-BR': 'Bloquear e desbloquear parceiros pela área comercial (Compras)',
      en: 'Block and unblock partners for the commercial area (Purchasing)',
    },
    sensitive: true,
  },
  {
    code: 'partner.restriction.financial',
    module: 'partner',
    label: {
      'pt-BR': 'Bloquear e desbloquear parceiros pela área financeira',
      en: 'Block and unblock partners for the finance area',
    },
    sensitive: true,
  },
  {
    code: 'partner.restriction.legal',
    module: 'partner',
    label: {
      'pt-BR': 'Bloquear e desbloquear parceiros pela área jurídica',
      en: 'Block and unblock partners for the legal area',
    },
    sensitive: true,
  },
  {
    code: 'partner.bank.read',
    module: 'partner',
    label: {
      'pt-BR': 'Ver as contas bancárias dos parceiros (mascaradas)',
      en: 'View partner bank accounts (masked)',
    },
    sensitive: true,
  },
  {
    code: 'partner.bank.reveal',
    module: 'partner',
    label: {
      'pt-BR': 'Ver o número completo de contas bancárias (cada consulta é registrada)',
      en: 'View full bank account numbers (every view is logged)',
    },
    sensitive: true,
  },
  {
    code: 'partner.bank.manage',
    module: 'partner',
    label: {
      'pt-BR': 'Incluir, alterar e inativar contas bancárias de parceiros (Financeiro)',
      en: 'Add, change and deactivate partner bank accounts (Finance)',
    },
    sensitive: true,
  },
  {
    code: 'partner.bank.approve',
    module: 'partner',
    label: {
      'pt-BR': 'Confirmar contas bancárias incluídas ou alteradas por outra pessoa (Tesouraria)',
      en: 'Confirm bank accounts added or changed by someone else (Treasury)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'partner.portal.read',
    module: 'partner',
    label: {
      'pt-BR': 'Ver quem tem acesso ao Portal do Fornecedor',
      en: 'View who has access to the Supplier Portal',
    },
  },
  {
    code: 'partner.portal.invite',
    module: 'partner',
    label: {
      'pt-BR': 'Convidar contatos de fornecedores homologados para o Portal do Fornecedor',
      en: 'Invite contacts of approved suppliers to the Supplier Portal',
    },
    sensitive: true,
  },
  {
    code: 'partner.portal.revoke',
    module: 'partner',
    label: {
      'pt-BR': 'Revogar o acesso de fornecedores ao Portal do Fornecedor',
      en: 'Revoke supplier access to the Supplier Portal',
    },
    sensitive: true,
  },
  {
    code: 'partner.payment_terms.manage',
    module: 'partner',
    label: {
      'pt-BR': 'Definir as condições de pagamento do parceiro na empresa (Financeiro)',
      en: 'Set the partner payment terms in the company (Finance)',
    },
    sensitive: true,
  },
  {
    code: 'partner.document_type.manage',
    module: 'partner',
    label: {
      'pt-BR': 'Configurar categorias de parceiro e tipos de documento exigidos',
      en: 'Configure partner categories and required document types',
    },
    sensitive: true,
  },
  {
    code: 'partner.document.review_commercial',
    module: 'partner',
    label: {
      'pt-BR': 'Analisar (aprovar ou rejeitar) documentos de parceiros da área comercial',
      en: 'Review (approve or reject) partner documents for the commercial area',
    },
    sensitive: true,
  },
  {
    code: 'partner.document.review_financial',
    module: 'partner',
    label: {
      'pt-BR': 'Analisar (aprovar ou rejeitar) documentos de parceiros da área financeira',
      en: 'Review (approve or reject) partner documents for the finance area',
    },
    sensitive: true,
  },
  {
    code: 'partner.document.review_legal',
    module: 'partner',
    label: {
      'pt-BR': 'Analisar (aprovar ou rejeitar) documentos de parceiros da área jurídica',
      en: 'Review (approve or reject) partner documents for the legal area',
    },
    sensitive: true,
  },
  {
    code: 'partner.document.restricted',
    module: 'partner',
    label: {
      'pt-BR': 'Baixar documentos restritos de parceiros (cada download é registrado)',
      en: 'Download restricted partner documents (every download is logged)',
    },
    sensitive: true,
  },
  {
    code: 'knowledge.document.read',
    module: 'knowledge',
    label: {
      'pt-BR': 'Consultar os documentos publicados da Central de Conhecimento',
      en: 'View published documents in the Knowledge Center',
    },
  },
  {
    code: 'knowledge.document.drafts',
    module: 'knowledge',
    label: {
      'pt-BR': 'Consultar versões em revisão (rascunhos) e o histórico dos documentos',
      en: 'View versions under review (drafts) and document history',
    },
  },
  {
    code: 'knowledge.document.review',
    module: 'knowledge',
    label: {
      'pt-BR': 'Revisar documentos pelos perfis revisores (de acordo ou ajustes)',
      en: 'Review documents as a reviewer profile (agree or request changes)',
    },
  },
  {
    code: 'knowledge.document.approve',
    module: 'knowledge',
    label: {
      'pt-BR': 'Aprovar ou rejeitar documentos pelo perfil aprovador',
      en: 'Approve or reject documents as the approver profile',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'knowledge.document.publish',
    module: 'knowledge',
    label: {
      'pt-BR': 'Publicar versões aprovadas e revogar a versão vigente',
      en: 'Publish approved versions and revoke the current one',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'knowledge.library.manage',
    module: 'knowledge',
    label: {
      'pt-BR': 'Administrar a biblioteca (problemas de importação e histórico)',
      en: 'Manage the library (import problems and history)',
    },
    sensitive: true,
  },
  {
    code: 'knowledge.restricted.finance',
    module: 'knowledge',
    label: {
      'pt-BR': 'Ver documentos restritos do Financeiro',
      en: 'View restricted Finance documents',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'knowledge.restricted.legal',
    module: 'knowledge',
    label: {
      'pt-BR': 'Ver documentos restritos do Jurídico',
      en: 'View restricted Legal documents',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'knowledge.restricted.hr',
    module: 'knowledge',
    label: {
      'pt-BR': 'Ver documentos restritos de Recursos Humanos',
      en: 'View restricted HR documents',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'knowledge.restricted.personnel',
    module: 'knowledge',
    label: {
      'pt-BR': 'Ver documentos restritos do Departamento Pessoal',
      en: 'View restricted Personnel documents',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'approval.governance.manage',
    module: 'approval',
    label: {
      'pt-BR':
        'Configurar níveis, responsáveis e matriz de alçadas de aprovação (administradores autorizados)',
      en: 'Configure approval levels, responsibles and authority matrix (authorized administrators)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'approval.request.decide',
    module: 'approval',
    label: {
      'pt-BR':
        'Decidir aprovações (aprovar, recusar ou devolver) nas etapas em que a pessoa é aprovadora',
      en: 'Decide approvals (approve, reject or return) on the steps where the person is an approver',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'structure.catalog.read',
    module: 'structure',
    label: {
      'pt-BR': 'Ver a Estrutura Funcional (tabelas corporativas de classificação)',
      en: 'View the functional structure (corporate classification tables)',
    },
  },
  {
    code: 'structure.catalog.create',
    module: 'structure',
    label: {
      'pt-BR': 'Incluir registros nas tabelas corporativas de classificação',
      en: 'Add records to the corporate classification tables',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'structure.catalog.update',
    module: 'structure',
    label: {
      'pt-BR':
        'Alterar registros e códigos de referência das tabelas corporativas (inclui subsidiárias)',
      en: 'Edit records and reference codes of the corporate tables (includes subsidiaries)',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'structure.catalog.status',
    module: 'structure',
    label: {
      'pt-BR': 'Ativar e inativar registros das tabelas corporativas de classificação',
      en: 'Activate and deactivate records of the corporate classification tables',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'structure.catalog.admin',
    module: 'structure',
    label: {
      'pt-BR': 'Administrar as categorias de classificação (criar novas tabelas corporativas)',
      en: 'Manage classification categories (create new corporate tables)',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.request.create',
    module: 'legal',
    label: {
      'pt-BR': 'Abrir solicitações jurídicas na empresa',
      en: 'Open legal requests in the company',
    },
  },
  {
    code: 'legal.request.read',
    module: 'legal',
    label: {
      'pt-BR': 'Ver as solicitações jurídicas da empresa (equipe jurídica)',
      en: "View the company's legal requests (legal team)",
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.request.manage',
    module: 'legal',
    label: {
      'pt-BR': 'Conduzir solicitações jurídicas (situação, participantes e anexos)',
      en: 'Handle legal requests (status, participants and attachments)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.request.assign',
    module: 'legal',
    label: {
      'pt-BR': 'Distribuir solicitações jurídicas (responsável e sigilo abaixo do padrão)',
      en: 'Distribute legal requests (assignee and confidentiality below the default)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.request.approve',
    module: 'legal',
    label: {
      'pt-BR': 'Dar a aprovação jurídica das solicitações',
      en: 'Give the legal approval of requests',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.restricted.read',
    module: 'legal',
    label: {
      'pt-BR': 'Ver solicitações jurídicas restritas (quando responsável ou participante)',
      en: 'View restricted legal requests (when assignee or participant)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.confidential.read',
    module: 'legal',
    label: {
      'pt-BR': 'Ver solicitações e anexos jurídicos confidenciais',
      en: 'View confidential legal requests and attachments',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.compartment.labor',
    module: 'legal',
    label: {
      'pt-BR': 'Ver assuntos trabalhistas no Jurídico',
      en: 'View labor matters in Legal',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.compartment.personal_data',
    module: 'legal',
    label: {
      'pt-BR': 'Ver assuntos de dados pessoais (LGPD) no Jurídico',
      en: 'View personal data (privacy) matters in Legal',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.compartment.litigation',
    module: 'legal',
    label: {
      'pt-BR': 'Ver assuntos de contencioso no Jurídico',
      en: 'View litigation matters in Legal',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.compartment.corporate',
    module: 'legal',
    label: {
      'pt-BR': 'Ver assuntos societários no Jurídico',
      en: 'View corporate matters in Legal',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.template.read',
    module: 'legal',
    label: {
      'pt-BR': 'Ver a Biblioteca Jurídica (minutas, cláusulas e modelos padrão e versões)',
      en: 'View the Legal Library (standard drafts, clauses and models, and versions)',
    },
    explicitOnly: true,
  },
  {
    code: 'legal.template.manage',
    module: 'legal',
    label: {
      'pt-BR':
        'Cadastrar e editar minutas, cláusulas e modelos padrão e enviar versões para aprovação',
      en: 'Register and edit standard drafts, clauses and models and send versions for approval',
    },
    explicitOnly: true,
  },
  {
    code: 'legal.template.approve',
    module: 'legal',
    label: {
      'pt-BR':
        'Aprovar ou devolver versões de minutas, cláusulas e modelos padrão (nunca as próprias)',
      en: 'Approve or return versions of standard drafts, clauses and models (never your own)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'legal.catalog.manage',
    module: 'legal',
    label: {
      'pt-BR': 'Administrar o catálogo e os formulários jurídicos',
      en: 'Manage the legal catalog and forms',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'facilities.request.create',
    module: 'facilities',
    label: {
      'pt-BR': 'Abrir chamados de Facilities na empresa',
      en: 'Open facilities requests in the company',
    },
  },
  {
    code: 'facilities.request.read',
    module: 'facilities',
    label: {
      'pt-BR': 'Ver os chamados de Facilities da empresa (equipe de Facilities)',
      en: "View the company's facilities requests (facilities team)",
    },
  },
  {
    code: 'facilities.request.manage',
    module: 'facilities',
    label: {
      'pt-BR':
        'Atender chamados de Facilities (triagem, classificação, responsável, execução e registro de pendências)',
      en: 'Handle facilities requests (triage, classification, assignee, execution and pending items)',
    },
  },
  {
    code: 'facilities.request.supervise',
    module: 'facilities',
    label: {
      'pt-BR': 'Encerrar chamados de Facilities sem o aceite do solicitante (com justificativa)',
      en: "Close facilities requests without the requester's acceptance (with justification)",
    },
    sensitive: true,
  },
  {
    code: 'facilities.catalog.manage',
    module: 'facilities',
    label: {
      'pt-BR': 'Administrar o catálogo e os formulários de Facilities',
      en: 'Manage the facilities catalog and forms',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'facilities.work_order.read',
    module: 'facilities',
    label: {
      'pt-BR': 'Ver as ordens de serviço e o histórico de manutenção dos bens da empresa',
      en: "View the company's work orders and asset maintenance history",
    },
  },
  {
    code: 'facilities.work_order.manage',
    module: 'facilities',
    label: {
      'pt-BR':
        'Criar, planejar e executar ordens de serviço (horas, materiais, checklist, ocorrências, conclusão e cancelamento)',
      en: 'Create, plan and run work orders (hours, materials, checklist, occurrences, completion and cancellation)',
    },
  },
  {
    code: 'facilities.work_order.accept',
    module: 'facilities',
    label: {
      'pt-BR': 'Aceitar ou recusar ordens de serviço concluídas e avaliar o prestador',
      en: 'Accept or reject completed work orders and rate the provider',
    },
  },
  {
    code: 'facilities.maintenance_plan.manage',
    module: 'facilities',
    label: {
      'pt-BR':
        'Administrar os planos de manutenção preventiva (criar, alterar, suspender, gerar as OS e pular datas)',
      en: 'Manage preventive maintenance plans (create, change, pause, generate work orders and skip dates)',
    },
  },
  {
    code: 'facilities.checklist.manage',
    module: 'facilities',
    label: {
      'pt-BR': 'Administrar os modelos de checklist de manutenção (rascunho e publicação)',
      en: 'Manage maintenance checklist templates (draft and publication)',
    },
    groupOnly: true,
    sensitive: true,
  },
  {
    code: 'facilities.pmoc.manage',
    module: 'facilities',
    label: {
      'pt-BR':
        'Administrar o PMOC (equipamentos de climatização, atividades, responsável técnico e documentos)',
      en: 'Manage the HVAC maintenance plan (PMOC): equipment, activities, technical lead and documents',
    },
  },
  {
    code: 'facilities.pmoc.validate',
    module: 'facilities',
    label: {
      'pt-BR': 'Registrar a validação do PMOC pelo responsável técnico',
      en: "Record the technical lead's validation of the PMOC",
    },
    sensitive: true,
  },
  {
    code: 'facilities.services.manage',
    module: 'facilities',
    label: {
      'pt-BR':
        'Administrar os serviços administrativos de Facilities (garagem, chaves, recebimento, expedição e viagens)',
      en: 'Manage Facilities administrative services (parking, keys, receiving, dispatch and travel)',
    },
  },
  {
    code: 'facilities.travel_policy.read',
    module: 'facilities',
    label: {
      'pt-BR': 'Consultar a Política Corporativa de Viagens (versões, limites e regras)',
      en: 'View the Corporate Travel Policy (versions, limits and rules)',
    },
  },
  {
    code: 'facilities.travel_policy.manage',
    module: 'facilities',
    label: {
      'pt-BR': 'Editar a Política Corporativa de Viagens (rascunhos, limites, prazos e regras)',
      en: 'Edit the Corporate Travel Policy (drafts, limits, deadlines and rules)',
    },
  },
  {
    code: 'facilities.travel_policy.approve',
    module: 'facilities',
    label: {
      'pt-BR': 'Aprovar e pôr em vigor uma versão da Política Corporativa de Viagens',
      en: 'Approve and activate a version of the Corporate Travel Policy',
    },
    sensitive: true,
  },
  {
    code: 'facilities.material.manage',
    module: 'facilities',
    label: {
      'pt-BR':
        'Administrar os materiais de Facilities (itens, saldos, entradas, transferências, ajustes e entregas de EPI)',
      en: 'Manage Facilities materials (items, balances, receipts, transfers, adjustments and PPE deliveries)',
    },
  },
  {
    code: 'asset.asset.read',
    module: 'asset',
    label: {
      'pt-BR': 'Consultar o patrimônio das empresas autorizadas',
      en: 'View the assets of the authorized companies',
    },
  },
  {
    code: 'asset.facilities.manage',
    module: 'asset',
    label: {
      'pt-BR': 'Cadastrar e movimentar bens do domínio Facilities',
      en: 'Register and move facilities-domain assets',
    },
  },
  {
    code: 'asset.it.manage',
    module: 'asset',
    label: {
      'pt-BR': 'Cadastrar e movimentar bens do domínio TI',
      en: 'Register and move IT-domain assets',
    },
  },
  {
    code: 'asset.accounting.manage',
    module: 'asset',
    label: {
      'pt-BR': 'Manter os dados contábeis dos bens (imobilizado, valor contábil, NetSuite)',
      en: 'Maintain asset accounting data (fixed asset, book value, NetSuite)',
    },
    sensitive: true,
  },
  {
    code: 'asset.inventory.manage',
    module: 'asset',
    label: {
      'pt-BR': 'Abrir, contar e fechar inventários patrimoniais',
      en: 'Open, count and close asset inventories',
    },
  },
  {
    code: 'asset.import',
    module: 'asset',
    label: {
      'pt-BR': 'Fazer a carga inicial de bens por planilha',
      en: 'Load assets from a spreadsheet',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'asset.class.manage',
    module: 'asset',
    label: {
      'pt-BR': 'Administrar as classes de bens e os campos específicos',
      en: 'Manage asset classes and specific fields',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'asset.policy.manage',
    module: 'asset',
    label: {
      'pt-BR': 'Definir a política do valor de referência para baixas e cessões',
      en: 'Set the reference value policy for disposals and transfers',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'it.catalog.read',
    module: 'it',
    label: {
      'pt-BR': 'Ver o catálogo de softwares (fabricantes, categorias, softwares e planos)',
      en: 'View the software catalog (manufacturers, categories, software and plans)',
    },
  },
  {
    code: 'it.catalog.manage',
    module: 'it',
    label: {
      'pt-BR': 'Administrar o catálogo de softwares (fabricantes, categorias, softwares e planos)',
      en: 'Manage the software catalog (manufacturers, categories, software and plans)',
    },
    groupOnly: true,
  },
  {
    code: 'it.subscription.read',
    module: 'it',
    label: {
      'pt-BR':
        'Ver as contratações de software e SaaS da empresa, com valores, licenças, atribuições e indicadores',
      en: "View the company's software and SaaS contracts, with amounts, licenses, assignments and indicators",
    },
  },
  {
    code: 'it.subscription.manage',
    module: 'it',
    label: {
      'pt-BR':
        'Cadastrar e alterar contratações de software e SaaS: valores, lotes de licenças, renovação, suspensão, cancelamento e anexos',
      en: 'Create and edit software and SaaS contracts: amounts, license lots, renewal, suspension, cancellation and attachments',
    },
    sensitive: true,
  },
  {
    code: 'it.license.assign',
    module: 'it',
    label: {
      'pt-BR': 'Atribuir, transferir, suspender e recuperar licenças na empresa',
      en: 'Assign, transfer, suspend and recover licenses in the company',
    },
  },
  // TI, Fase 2 (ADR 0037): parte técnica dos equipamentos do patrimônio compartilhado. Ver o bem
  // continua pelas regras do patrimônio; movimentações, pela permissão asset.it.manage.
  {
    code: 'it.device.read',
    module: 'it',
    label: {
      'pt-BR': 'Ver a ficha técnica, os identificadores, os reparos e os checklists de devolução',
      en: 'View technical profiles, identifiers, repairs and return checklists',
    },
  },
  {
    code: 'it.device.manage',
    module: 'it',
    label: {
      'pt-BR':
        'Administrar a parte técnica dos equipamentos de TI: ficha, identificadores, reparos e checklist de devolução',
      en: 'Manage the technical side of IT equipment: profile, identifiers, repairs and return checklist',
    },
  },
  {
    // Importar contratações, lotes e atribuições por planilha (TI, B0.6; D-0.4): grava em massa e
    // tem valores, por isso é sensível (segunda etapa) e fica inicialmente só com a gestão de TI.
    code: 'it.license.import',
    module: 'it',
    label: {
      'pt-BR': 'Importar contratações, lotes e atribuições de licenças por planilha',
      en: 'Import software subscriptions, license lots and assignments from a spreadsheet',
    },
    sensitive: true,
  },
  // TI, Financeiro (Fase 3 revisada): dados financeiros, por isso sensíveis (segunda etapa).
  // TI, Acessos (A1, A2, A7): quem tem acesso a quê; sensíveis (segunda etapa).
  {
    code: 'it.access.read',
    module: 'it',
    label: {
      'pt-BR': 'Ver os sistemas e os acessos concedidos aos colaboradores',
      en: 'View systems and the access granted to employees',
    },
    sensitive: true,
  },
  {
    code: 'it.access.manage',
    module: 'it',
    label: {
      'pt-BR': 'Cadastrar sistemas e pedir, conceder e revogar acessos',
      en: 'Register systems and request, grant and revoke access',
    },
    sensitive: true,
  },
  {
    code: 'it.governance.read',
    module: 'it',
    label: {
      'pt-BR': 'Ver controles, evidências, exceções, RACI e o cofre de emergência da TI',
      en: 'View IT controls, evidence, exceptions, RACI and the emergency vault register',
    },
  },
  {
    code: 'it.governance.manage',
    module: 'it',
    label: {
      'pt-BR': 'Cadastrar controles, evidências, exceções, RACI e o cofre de emergência da TI',
      en: 'Manage IT controls, evidence, exceptions, RACI and the emergency vault register',
    },
    sensitive: true,
  },
  {
    code: 'it.incident.manage',
    module: 'it',
    label: {
      'pt-BR': 'Registrar e tratar incidentes de segurança (sigilosos)',
      en: 'Record and handle security incidents (confidential)',
    },
    sensitive: true,
  },
  {
    code: 'it.project.read',
    module: 'it',
    label: {
      'pt-BR': 'Ver os projetos e as mudanças da TI',
      en: 'View IT projects and changes',
    },
  },
  {
    code: 'it.project.manage',
    module: 'it',
    label: {
      'pt-BR': 'Cadastrar projetos e registrar mudanças da TI (a aprovação é do gestor do centro)',
      en: 'Manage IT projects and record changes (approval is by the cost center manager)',
    },
  },
  {
    code: 'it.finance.read',
    module: 'it',
    label: {
      'pt-BR':
        'Ver os serviços, as faturas, os custos por competência, o rateio e o painel financeiro da TI',
      en: 'View IT services, invoices, costs by period, allocation and the IT finance dashboard',
    },
    sensitive: true,
  },
  {
    code: 'it.service.manage',
    module: 'it',
    label: {
      'pt-BR': 'Cadastrar e alterar os serviços contratados de TI e as cobranças deles',
      en: 'Create and edit contracted IT services and their charges',
    },
    sensitive: true,
  },
  {
    code: 'it.invoice.manage',
    module: 'it',
    label: {
      'pt-BR': 'Registrar, conferir e contestar as faturas de TI',
      en: 'Record, check and dispute IT invoices',
    },
    sensitive: true,
  },
  {
    code: 'it.allocation.manage',
    module: 'it',
    label: {
      'pt-BR': 'Apurar o custo da TI por competência e o rateio por centro de custo',
      en: 'Compute IT cost by period and the allocation by cost center',
    },
    sensitive: true,
  },
  {
    code: 'it.period.close',
    module: 'it',
    label: {
      'pt-BR': 'Fechar e reabrir o mês da TI (reabrir exige motivo)',
      en: 'Close and reopen the IT month (reopening requires a reason)',
    },
    sensitive: true,
  },
  {
    code: 'it.invoice.confirm_payment',
    module: 'it',
    label: {
      'pt-BR': 'Confirmar o pagamento das faturas de TI (Financeiro, não a TI)',
      en: 'Confirm payment of IT invoices (Finance, not IT)',
    },
    sensitive: true,
  },
  {
    code: 'it.device.import',
    module: 'it',
    label: {
      'pt-BR': 'Importar dados técnicos dos equipamentos de TI por planilha',
      en: 'Import technical data of IT equipment from a spreadsheet',
    },
    sensitive: true,
  },
  {
    code: 'it.repair.cost.read',
    module: 'it',
    label: {
      'pt-BR': 'Ver e informar o custo dos reparos de equipamentos de TI',
      en: 'View and record the cost of IT equipment repairs',
    },
    sensitive: true,
  },
  // Service Desk da TI (ADR 0082). Abrir e acompanhar os próprios chamados exige só o login.
  {
    code: 'it.ticket.work',
    module: 'it',
    label: {
      'pt-BR': 'Triar e atender chamados de TI das filas comuns (Suporte e Infraestrutura)',
      en: 'Triage and handle IT tickets in the common queues (Support and Infrastructure)',
    },
  },
  {
    code: 'it.ticket.security',
    module: 'it',
    label: {
      'pt-BR': 'Ver e tratar os chamados sigilosos da fila Segurança e LGPD',
      en: 'View and handle the confidential tickets of the Security and privacy queue',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'it.ticket.manage',
    module: 'it',
    label: {
      'pt-BR': 'Administrar o catálogo de serviços, os formulários e as filas do Service Desk',
      en: 'Manage the Service Desk service catalog, forms and queues',
    },
    groupOnly: true,
    sensitive: true,
  },
  // FP&A (ADR 0032). Dados financeiros: todas sensíveis e explícitas (o Administrador técnico não
  // as recebe sozinho). Quais empresas, unidades, departamentos e centros de custo cada pessoa vê
  // vem dos acessos do FP&A (Fase 1B), conferidos no banco.
  {
    code: 'fpa.data.read',
    module: 'fpa',
    label: {
      'pt-BR': 'Ver os números do FP&A (orçado, realizado, projetado, DRE e relatórios)',
      en: 'View FP&A figures (budget, actuals, projections, P&L and reports)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.plan.edit',
    module: 'fpa',
    label: {
      'pt-BR': 'Elaborar e revisar Budget e Forecast',
      en: 'Prepare and review Budget and Forecast',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.plan.manage',
    module: 'fpa',
    label: {
      'pt-BR': 'Conduzir versões: consolidar, enviar para aprovação e publicar',
      en: 'Run versions: consolidate, submit for approval and publish',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.plan.approve',
    module: 'fpa',
    label: {
      'pt-BR': 'Aprovar Budget e Forecast conforme as alçadas',
      en: 'Approve Budget and Forecast according to the approval matrix',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.actual.manage',
    module: 'fpa',
    label: {
      'pt-BR': 'Lançar e importar o realizado',
      en: 'Record and import actuals',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.actual.audit',
    module: 'fpa',
    label: {
      'pt-BR': 'Auditar pagamentos e registrar no Realizado',
      en: 'Audit payments and record them in actuals',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.actual.origin.manage',
    module: 'fpa',
    label: {
      'pt-BR': 'Manter as origens do Realizado e o mês de virada de cada empresa',
      en: 'Manage the sources of actuals and each company cutover month',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.settings.manage',
    module: 'fpa',
    label: {
      'pt-BR': 'Administrar premissas, cenários e o calendário orçamentário',
      en: 'Manage assumptions, scenarios and the budget calendar',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.period.close',
    module: 'fpa',
    label: {
      'pt-BR': 'Fechar e reabrir períodos do planejamento',
      en: 'Close and reopen planning periods',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.access.manage',
    module: 'fpa',
    label: {
      'pt-BR':
        'Administrar os acessos do FP&A (empresas, unidades, departamentos e centros de custo)',
      en: 'Manage FP&A access (companies, units, departments and cost centers)',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.payroll.read',
    module: 'fpa',
    label: {
      'pt-BR': 'Ver salários e dados individuais no planejamento de pessoal',
      en: 'View salaries and individual data in workforce planning',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'fpa.payroll.manage',
    module: 'fpa',
    label: {
      'pt-BR': 'Planejar salários e dados individuais no planejamento de pessoal',
      en: 'Plan salaries and individual data in workforce planning',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    // Reportar não exige permissão: qualquer pessoa logada reporta e vê os próprios reportes.
    code: 'feedback.report.manage',
    module: 'feedback',
    label: {
      'pt-BR': 'Ver e tratar todos os bugs e melhorias reportados',
      en: 'View and handle all reported bugs and improvements',
    },
    groupOnly: true,
  },
  // Intranet (ADR 0075): ler o que está publicado não exige permissão (qualquer pessoa logada).
  {
    code: 'intranet.content.manage',
    module: 'intranet',
    label: {
      'pt-BR':
        'Cadastrar e publicar comunicados, novidades, agenda, políticas e normativas e links úteis da intranet',
      en: 'Create and publish intranet announcements, news, calendar, policies and useful links',
    },
    groupOnly: true,
  },
  // Dashboard executivo (ADR 0097): só abre a tela. Cada cartão continua dependendo da lista que
  // ele abre. Explícita: o Administrador não recebe; quem vê é quem for autorizado.
  {
    code: 'executive.dashboard.view',
    module: 'executive',
    label: { 'pt-BR': 'Abrir o Dashboard executivo', en: 'Open the executive dashboard' },
    explicitOnly: true,
  },
  // Dados Mestres: Pessoas, Colaboradores e Cargos (ADR 0039). Dados pessoais restritos e
  // salários são explícitos (o Administrador não recebe) e ninguém os concede a si mesmo.
  {
    code: 'people.org.read',
    module: 'people',
    label: {
      'pt-BR':
        'Consultar pessoas, colaboradores, organograma e catálogo de cargos (sem dados restritos)',
      en: 'View people, employees, org chart and job catalog (no restricted data)',
    },
  },
  {
    code: 'people.person.manage',
    module: 'people',
    label: {
      'pt-BR': 'Cadastrar e alterar pessoas (nome, e-mail corporativo, situação, usuário ligado)',
      en: 'Create and edit people (name, corporate e-mail, status, linked user)',
    },
    sensitive: true,
  },
  {
    code: 'people.private.read',
    module: 'people',
    label: {
      'pt-BR':
        'Ver dados pessoais restritos (CPF, nascimento, endereço, contatos pessoais); fica registrado',
      en: 'View restricted personal data (tax ID, birth date, address, personal contacts); logged',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'people.private.manage',
    module: 'people',
    label: {
      'pt-BR': 'Alterar dados pessoais restritos (CPF, nascimento, endereço, contatos pessoais)',
      en: 'Edit restricted personal data (tax ID, birth date, address, personal contacts)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'people.preferences.manage',
    module: 'people',
    label: {
      'pt-BR':
        'Ver e alterar tamanhos (blusa e calçado) e restrições alimentares do colaborador; fica registrado',
      en: 'View and edit sizes (shirt and shoes) and dietary restrictions of employees; logged',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'people.access.grant',
    module: 'people',
    label: {
      'pt-BR':
        'Dar acesso ao sistema aos colaboradores em lote (usuário, perfil Colaborador e conta de login)',
      en: 'Give employees access to the system in bulk (user, Employee profile and login account)',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'people.payroll_cost.manage',
    module: 'people',
    label: {
      'pt-BR': 'Importar e consultar o custo da folha da ADP (totais por centro de custo e mês)',
      en: 'Import and view the ADP payroll cost (totals by cost center and month)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'people.hr_request.work',
    module: 'people',
    label: {
      'pt-BR':
        'Atender as solicitações ao RH/DP (declarações, correções e dúvidas dos colaboradores)',
      en: 'Handle HR requests (employee letters, corrections and questions)',
    },
    explicitOnly: true,
  },
  {
    code: 'people.training.manage',
    module: 'people',
    label: {
      'pt-BR': 'Cadastrar os treinamentos obrigatórios (versão e validade)',
      en: 'Manage mandatory trainings (version and validity)',
    },
  },
  {
    code: 'people.acknowledgement.read',
    module: 'people',
    label: {
      'pt-BR': 'Ver quem falta dar ciência das políticas e dos treinamentos',
      en: 'See who still has to acknowledge policies and trainings',
    },
  },
  {
    code: 'people.position_request.manage',
    module: 'people',
    label: {
      'pt-BR': 'Pedir e acompanhar requisições de vaga de qualquer centro de custo (RH/DP)',
      en: 'Request and follow job requisitions for any cost center (HR)',
    },
  },
  {
    code: 'people.mailing.read',
    module: 'people',
    label: {
      'pt-BR':
        'Ver nome e endereço de entrega dos colaboradores ativos, para envio de lembranças; fica registrado',
      en: 'View name and delivery address of active employees, to send gifts; logged',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'people.employee.manage',
    module: 'people',
    label: {
      'pt-BR': 'Administrar colaboradores: vínculo, movimentações, afastamentos e tipos de vínculo',
      en: 'Manage employees: employment, movements, leaves and employment types',
    },
    sensitive: true,
  },
  {
    code: 'people.allocation.manage',
    module: 'people',
    label: {
      'pt-BR': 'Administrar os rateios de colaboradores entre centros de custo',
      en: 'Manage employee cost center allocations',
    },
    sensitive: true,
  },
  {
    code: 'career.catalog.manage',
    module: 'career',
    label: {
      'pt-BR':
        'Administrar o catálogo de cargos (famílias, subfamílias, JL, trilhas, grades e cargos)',
      en: 'Manage the job catalog (families, subfamilies, levels, tracks, grades and jobs)',
    },
    groupOnly: true,
    sensitive: true,
  },
  {
    code: 'career.salary.read',
    module: 'career',
    label: {
      'pt-BR': 'Ver os valores das grades e estruturas salariais',
      en: 'View salary grade and structure amounts',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'career.salary.manage',
    module: 'career',
    label: {
      'pt-BR': 'Administrar os valores das grades e estruturas salariais',
      en: 'Manage salary grade and structure amounts',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  // Central de Importações de Dados Mestres (ADR 0040). A importação grava pelos cadastros de
  // origem: cada linha exige também a permissão do cadastro (ex.: career.catalog.manage).
  {
    code: 'masterdata.import.read',
    module: 'masterdata',
    label: {
      'pt-BR': 'Consultar a Central de Importações, baixar templates vazios e ver o histórico',
      en: 'View the Import Center, download empty templates and see the history',
    },
  },
  {
    code: 'masterdata.import.export',
    module: 'masterdata',
    label: {
      'pt-BR': 'Baixar templates com os registros existentes (para atualização)',
      en: 'Download templates with existing records (for updates)',
    },
    sensitive: true,
  },
  {
    code: 'masterdata.import.validate',
    module: 'masterdata',
    label: {
      'pt-BR': 'Enviar planilhas para pré-validação na Central de Importações',
      en: 'Upload spreadsheets for pre-validation in the Import Center',
    },
    sensitive: true,
  },
  {
    code: 'masterdata.import.apply',
    module: 'masterdata',
    label: {
      'pt-BR': 'Confirmar importações validadas (grava nos cadastros)',
      en: 'Confirm validated imports (writes to the records)',
    },
    sensitive: true,
  },
  {
    code: 'masterdata.import.restricted',
    module: 'masterdata',
    label: {
      'pt-BR':
        'Importar dados pessoais restritos (CPF, nascimento, contatos, endereço); exige também alterar dados pessoais',
      en: 'Import restricted personal data (tax ID, birth date, contacts, address); also requires editing personal data',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'masterdata.import.salary',
    module: 'masterdata',
    label: {
      'pt-BR': 'Importar valores salariais das grades (exige também os valores salariais)',
      en: 'Import salary grade amounts (also requires salary amounts)',
    },
    groupOnly: true,
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'finance.bank_account.read',
    module: 'finance',
    label: {
      'pt-BR': 'Ver as contas bancárias da empresa (número mascarado)',
      en: 'View the company bank accounts (masked number)',
    },
  },
  {
    code: 'finance.bank_account.manage',
    module: 'finance',
    label: {
      'pt-BR': 'Incluir, alterar e inativar contas bancárias da empresa (Tesouraria)',
      en: 'Add, change and deactivate company bank accounts (Treasury)',
    },
    sensitive: true,
  },
  {
    code: 'finance.bank_account.approve',
    module: 'finance',
    label: {
      'pt-BR': 'Confirmar contas da empresa incluídas ou alteradas por outra pessoa (Tesouraria)',
      en: 'Confirm company bank accounts added or changed by someone else (Treasury)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'finance.bank_account.reveal',
    module: 'finance',
    label: {
      'pt-BR': 'Ver o número completo das contas da empresa (registrado)',
      en: 'View the full number of company bank accounts (logged)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'finance.payable.read',
    module: 'finance',
    label: { 'pt-BR': 'Ver títulos a pagar e pagamentos', en: 'View payables and payments' },
    sensitive: true,
  },
  {
    code: 'finance.payable.manage',
    module: 'finance',
    label: {
      'pt-BR': 'Lançar títulos a pagar, enviar para aprovação e cancelar saldo',
      en: 'Enter payables, send them for approval and cancel balances',
    },
    sensitive: true,
  },
  {
    code: 'finance.payable.schedule',
    module: 'finance',
    label: { 'pt-BR': 'Programar o pagamento de parcelas', en: 'Schedule installment payments' },
    sensitive: true,
  },
  {
    code: 'finance.payment.register',
    module: 'finance',
    label: {
      'pt-BR': 'Registrar e estornar pagamentos (Tesouraria)',
      en: 'Record and reverse payments (Treasury)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'finance.payable.intake',
    module: 'finance',
    label: {
      'pt-BR': 'Lançar nota de fornecedor recebida fora do portal',
      en: 'Enter supplier invoices received outside the portal',
    },
    sensitive: true,
  },
  {
    code: 'finance.treasury.read',
    module: 'finance',
    label: {
      'pt-BR': 'Ver o caixa da empresa: extratos, saldos, ajustes e transferências (Tesouraria)',
      en: 'View the company cash: statements, balances, adjustments and transfers (Treasury)',
    },
  },
  {
    code: 'finance.treasury.movement.manage',
    module: 'finance',
    label: {
      'pt-BR':
        'Lançar no caixa da empresa: extratos, saldos informados, ajustes propostos e transferências',
      en: 'Enter company cash records: statements, reported balances, proposed adjustments and transfers',
    },
    sensitive: true,
  },
  {
    code: 'finance.treasury.adjustment.approve',
    module: 'finance',
    label: {
      'pt-BR': 'Confirmar ajustes de saldo propostos por outra pessoa (Tesouraria)',
      en: 'Confirm balance adjustments proposed by someone else (Treasury)',
    },
    sensitive: true,
    explicitOnly: true,
  },
  {
    code: 'finance.treasury.settings.manage',
    module: 'finance',
    label: {
      'pt-BR':
        'Definir os parâmetros do caixa da empresa (saldo mínimo e moeda da visão consolidada)',
      en: 'Set the company cash parameters (minimum balance and consolidated view currency)',
    },
    sensitive: true,
  },
  {
    code: 'finance.cashflow.forecast.manage',
    module: 'finance',
    label: {
      'pt-BR': 'Lançar, editar e vincular previsões de entrada e saída do caixa (Tesouraria)',
      en: 'Enter, edit and link expected cash inflows and outflows (Treasury)',
    },
    sensitive: true,
  },
  {
    code: 'finance.cashflow.scenario.manage',
    module: 'finance',
    label: {
      'pt-BR':
        'Montar, comparar e arquivar cenários de caixa (Tesouraria; nunca geram lançamentos)',
      en: 'Build, compare and archive cash scenarios (Treasury; they never create entries)',
    },
    sensitive: true,
  },
  {
    code: 'finance.treasury.reconcile',
    module: 'finance',
    label: {
      'pt-BR':
        'Conciliar o extrato com os pagamentos e transferências e desfazer conciliações (Tesouraria)',
      en: 'Reconcile the statement with payments and transfers and undo reconciliations (Treasury)',
    },
    sensitive: true,
  },
] as const satisfies readonly PermissionDefinition[];

export type PermissionCode = (typeof PERMISSIONS)[number]['code'];

export const PERMISSION_CODES: readonly PermissionCode[] = PERMISSIONS.map((p) => p.code);

/** O que o perfil Administrador recebe automaticamente: tudo, menos as permissões explícitas. */
export const ADMIN_PERMISSION_CODES: readonly PermissionCode[] = PERMISSIONS.filter(
  (p) => !(p as PermissionDefinition).explicitOnly,
).map((p) => p.code);

/**
 * Permissões sensíveis: exigem a segunda etapa de login (S2, ADR 0013 e ADR 0020). Sem ela, a
 * sessão perde essas permissões no servidor e no banco, e as telas orientam a ativá-la.
 */
export const SENSITIVE_PERMISSION_CODES: readonly PermissionCode[] = PERMISSIONS.filter(
  (p) => !!(p as PermissionDefinition).sensitive,
).map((p) => p.code);

/** Permissões que só funcionam quando o perfil é dado para o grupo todo. */
export const GROUP_ONLY_PERMISSION_CODES: readonly PermissionCode[] = PERMISSIONS.filter(
  (p) => !!(p as PermissionDefinition).groupOnly,
).map((p) => p.code);

/**
 * Perfil dado por empresa ou filial (auditoria de 27/10): as permissões "só do grupo" não valem.
 * Se todas as permissões do perfil são assim, a atribuição não faz nada e é recusada; se só
 * algumas, quem concede precisa confirmar que sabe disso.
 */
export function groupOnlyConflict(
  permissions: readonly string[],
  scopeType: 'group' | 'legal_entity' | 'branch',
): { groupOnly: string[]; useless: boolean } | null {
  if (scopeType === 'group') return null;
  const set = new Set<string>(GROUP_ONLY_PERMISSION_CODES);
  const groupOnly = permissions.filter((p) => set.has(p)).sort();
  if (groupOnly.length === 0) return null;
  return { groupOnly, useless: groupOnly.length === permissions.length };
}

export function isPermissionCode(value: string): value is PermissionCode {
  return (PERMISSION_CODES as readonly string[]).includes(value);
}

/** Onde uma atribuição de perfil vale. */
export type ScopeType = 'group' | 'legal_entity' | 'branch';

export interface RoleGrant {
  permissions: readonly string[];
  scopeType: ScopeType;
  legalEntityId: string | null;
  branchId: string | null;
  /** Empresa da filial, quando scopeType = 'branch'. */
  branchLegalEntityId: string | null;
}

export interface ScopeTarget {
  legalEntityId?: string | null;
  branchId?: string | null;
}

/**
 * Decide se um conjunto de atribuições dá a permissão no alvo.
 *
 * - Sem alvo: basta ter a permissão em algum escopo (usado para listar; o banco filtra as linhas).
 * - Alvo empresa: vale atribuição no grupo ou naquela empresa.
 * - Alvo filial: vale atribuição no grupo, na empresa da filial ou na própria filial.
 */
export function hasPermission(
  grants: readonly RoleGrant[],
  permission: PermissionCode,
  target?: ScopeTarget,
): boolean {
  const relevant = grants.filter((g) => g.permissions.includes(permission));
  if (relevant.length === 0) return false;
  if (!target || (!target.legalEntityId && !target.branchId)) return true;

  return relevant.some((g) => {
    if (g.scopeType === 'group') return true;
    if (g.scopeType === 'legal_entity') {
      return target.legalEntityId != null && g.legalEntityId === target.legalEntityId;
    }
    // Atribuição numa filial só vale para aquela filial.
    return target.branchId != null && g.branchId === target.branchId;
  });
}

/** Permissões marcadas como "só no grupo" exigem atribuição com escopo de grupo. */
export function hasGroupPermission(grants: readonly RoleGrant[], permission: PermissionCode) {
  return grants.some((g) => g.scopeType === 'group' && g.permissions.includes(permission));
}
